import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './DepartmentScreen.css';
import './PackOutScreen.css';

/**
 * Pack-Out Station — the close-out seat at the end of the packaging table.
 * Scanner-first (no touchscreen): scan a cart barcode and the screen fills
 * with everything needed to final-QC and pack it — contents by work order,
 * live NetSuite status, and the exact components the build will consume.
 *
 * v1 is REVIEW-ONLY: builds are still posted by hand in NetSuite. The
 * confirm-and-post flow (with component swaps) arrives in v2 once this
 * screen has been verified against reality on live carts.
 */
function PackOutScreen() {
  const [scanValue, setScanValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [data, setData] = useState(null);
  const [actualCases, setActualCases] = useState({});

  const scanBuffer = useRef('');
  const scanTimeout = useRef(null);
  const inputRef = useRef(null);

  const loadCart = useCallback(async (barcode) => {
    const code = String(barcode || '').trim();
    if (!code) return;
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await axios.get(`/api/pack-out/cart/${encodeURIComponent(code)}`);
      setData(response.data);
      const defaults = {};
      for (const wo of response.data.workOrders || []) {
        const cases = wo.caseQty > 0 ? wo.unitsOnCart / wo.caseQty : wo.unitsOnCart;
        defaults[wo.workOrderNumber || wo.itemSku] = Math.round(cases * 100) / 100;
      }
      setActualCases(defaults);
    } catch (err) {
      setData(null);
      setErrorMessage(err.response?.data?.error || 'Lookup failed — try again');
    } finally {
      setLoading(false);
      setScanValue('');
    }
  }, []);

  // Global scanner capture: scanners type fast and end with Enter. Ignore
  // keystrokes aimed at the quantity inputs.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.key === 'Escape') {
        setData(null);
        setErrorMessage('');
        return;
      }
      if (e.key === 'Enter') {
        if (scanBuffer.current.length >= 4) loadCart(scanBuffer.current);
        scanBuffer.current = '';
        return;
      }
      if (e.key.length === 1) {
        scanBuffer.current += e.key;
        clearTimeout(scanTimeout.current);
        scanTimeout.current = setTimeout(() => { scanBuffer.current = ''; }, 250);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(scanTimeout.current);
    };
  }, [loadCart]);

  const fmtQty = (n) => {
    if (n == null || Number.isNaN(n)) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  };

  return (
    <div className="packout-screen">
      <header className="packout-header">
        <h1>PACK-OUT</h1>
        <div className="packout-v1-banner">
          Review only — post the build in NetSuite as usual. Confirm-and-post is coming next.
        </div>
      </header>

      {!data && (
        <div className="packout-idle">
          <div className="packout-scan-prompt">Scan a cart sheet to begin</div>
          <form
            className="packout-manual"
            onSubmit={(e) => { e.preventDefault(); loadCart(scanValue); }}
          >
            <input
              ref={inputRef}
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              placeholder="or type CART-… and press Enter"
              autoFocus
            />
          </form>
          {loading && <div className="packout-status">Looking up cart…</div>}
          {errorMessage && <div className="packout-error">{errorMessage}</div>}
        </div>
      )}

      {data && (
        <div className="packout-body">
          <div className="packout-cart-header">
            <div className="packout-cart-id">
              <span className="packout-cart-number">{data.cart.cartNumber}</span>
              <span className="packout-cart-meta">
                {data.cart.batchName}
                {data.cart.cartIndex ? ` · cart ${data.cart.cartIndex}${data.cart.totalCarts ? ` of ${data.cart.totalCarts}` : ''}` : ''}
                {data.cart.fragrance ? ` · ${data.cart.fragrance}` : ''}
              </span>
            </div>
            <div className="packout-cart-status">
              {data.cart.totalVessels || 0} vessels · cart status: {data.cart.status}
              {data.cart.closedReason ? ` (${data.cart.closedReason})` : ''}
            </div>
          </div>

          {(data.workOrders || []).map((wo) => {
            const key = wo.workOrderNumber || wo.itemSku;
            const cases = actualCases[key] ?? 0;
            return (
              <div className="packout-wo-card" key={key}>
                <div className="packout-wo-head">
                  <div>
                    <span className="packout-wo-number">WO {wo.workOrderNumber || '—'}</span>
                    <span className="packout-wo-item">{wo.itemSku} — {wo.itemDescription}</span>
                    {wo.customerName && <span className="packout-wo-customer">{wo.customerName}</span>}
                  </div>
                  <div className="packout-wo-ns">
                    {wo.nsStatus ? `NS: ${wo.nsStatus} · built ${fmtQty(wo.builtQty)} of ${fmtQty(wo.orderQty)}` : 'NetSuite status unavailable'}
                  </div>
                </div>

                {wo.memo && <div className="packout-memo">MEMO: {wo.memo}</div>}

                <div className="packout-qty-row">
                  <div>
                    On this cart: <b>{wo.unitsOnCart}</b> vessels
                    {wo.caseQty > 1 ? ` (${fmtQty(wo.unitsOnCart / wo.caseQty)} cases of ${wo.caseQty})` : ''}
                  </div>
                  <label className="packout-actual">
                    Packing (cases):
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={cases}
                      onChange={(e) => setActualCases({ ...actualCases, [key]: Number(e.target.value) })}
                    />
                  </label>
                </div>

                {wo.components && wo.components.length > 0 ? (
                  <table className="packout-bom">
                    <thead>
                      <tr><th>Component</th><th>Per case</th><th>For {fmtQty(cases)} cases</th></tr>
                    </thead>
                    <tbody>
                      {wo.components.map((c) => (
                        <tr key={c.sku} className={c.sku && c.sku.startsWith('Wick-') ? 'packout-wick-row' : ''}>
                          <td>{c.name || c.sku}</td>
                          <td>{fmtQty(c.perCase)}</td>
                          <td>{c.perCase != null ? fmtQty(c.perCase * cases) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="packout-no-bom">No component lines found on this work order.</div>
                )}
              </div>
            );
          })}

          <div className="packout-footer">
            Scan the next cart, or press Esc to clear.
            {loading && <span className="packout-status"> Looking up cart…</span>}
            {errorMessage && <span className="packout-error"> {errorMessage}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default PackOutScreen;
